import {
  selectVideoModeCopyTag,
  useTreeContext,
} from "@/components/ui/tree/tree-context";
import { selectLightweightElements } from "@/store/lightweight-elements-selectors";
import { LightweightElementsRecord } from "@/store/lightweight-elements-slice";
import { useAppSelector } from "@/store/store-hooks";
import { TreeData } from "@/utils/tree-generation/tree-data";
import {
  getFiltersToApply,
  iterateTreeDescendants,
} from "@/utils/tree-generation/tree-filters-utils";
import { GUID } from "@faro-lotv/foundation";
import { DateTime } from "luxon";
import { useMemo } from "react";

/** A contiguous slice of time (a calendar day or month) and how many captures fall into it */
export type CaptureBucket = {
  /** Start of the bucket, in local time */
  start: Date;
  /** How many captures were taken within the bucket */
  count: number;
};

/** Where the captures of a project sit on the calendar */
export type CaptureDateStats = {
  /** Days that contain at least one capture, oldest first */
  days: CaptureBucket[];
  /** Total number of captures counted across all days */
  total: number;
};

const EMPTY_STATS: CaptureDateStats = { days: [], total: 0 };

/**
 * The capture-date histogram of the project.
 *
 * A "capture" is one row of the project tree on one day, because that is the unit the tree
 * already presents to the user: a location stays a single row however many time points it
 * holds, a video-mode session is one row, and locations made purely of video-mode keyframes
 * are not rows at all. Counting anything finer produces numbers that contradict the panel
 * the filter is shared with.
 *
 * The tree is read from `TreeContext`, which the project page already builds, so nothing is
 * recomputed here and large projects pay no extra cost.
 *
 * @returns The days holding captures and the overall total
 */
export function useCaptureDateStats(): CaptureDateStats {
  const { tree } = useTreeContext();
  const elements = useAppSelector(selectLightweightElements);
  const videoModeCopyTagId = useAppSelector(selectVideoModeCopyTag);

  return useMemo(
    () => computeCaptureDateStats(tree, elements, videoModeCopyTagId),
    [tree, elements, videoModeCopyTagId],
  );
}

/**
 * @returns The capture-date histogram for a project tree
 * @param tree The rows the project tree displays
 * @param elements All lightweight elements, to resolve each row's descendants
 * @param videoModeCopyTagId Tag marking rooms copied for a video mode dataset
 */
export function computeCaptureDateStats(
  tree: TreeData[],
  elements: LightweightElementsRecord,
  videoModeCopyTagId?: GUID,
): CaptureDateStats {
  const countsPerDay = new Map<number, number>();
  let total = 0;

  const stack = [...tree];
  while (stack.length > 0) {
    const row = stack.pop();
    if (!row) continue;
    for (const day of captureDaysOfRow(row, elements, videoModeCopyTagId)) {
      countsPerDay.set(day, (countsPerDay.get(day) ?? 0) + 1);
      total++;
    }
    if (row.children) stack.push(...row.children);
  }

  if (total === 0) return EMPTY_STATS;

  const days = Array.from(countsPerDay, ([start, count]) => ({
    start: new Date(start),
    count,
  })).sort((a, b) => a.start.getTime() - b.start.getTime());

  return { days, total };
}

/**
 * Mirrors how the tree itself decides whether a row survives a date filter: a row that is
 * date-filterable answers with its own date, while a row that is not (a location, a cluster,
 * a dataset) is matched through its descendants instead.
 *
 * @returns The days on which the row holds captures, as start-of-day timestamps
 * @param row The tree row to inspect
 * @param elements All lightweight elements, to resolve the row's descendants
 * @param videoModeCopyTagId Tag marking rooms copied for a video mode dataset
 */
function captureDaysOfRow(
  row: TreeData,
  elements: LightweightElementsRecord,
  videoModeCopyTagId: GUID | undefined,
): Set<number> {
  const days = new Set<number>();

  // Rows without an element are purely visual folders
  if (!row.element) return days;

  if (getFiltersToApply(row.element, elements).date) {
    days.add(startOfDay(row.element.createdAt));
    return days;
  }

  // Only leaves are resolved through their descendants: a row with children is already
  // represented by those children, so counting it too would double up.
  if (row.children === null) {
    for (const child of iterateTreeDescendants(
      row.element.id,
      elements,
      videoModeCopyTagId,
    )) {
      if (getFiltersToApply(child, elements).date) {
        days.add(startOfDay(child.createdAt));
      }
    }
  }

  return days;
}

/**
 * @returns The start of the local calendar day holding `createdAt`, in milliseconds
 * @param createdAt The ISO timestamp to truncate
 */
function startOfDay(createdAt: string): number {
  return DateTime.fromISO(createdAt).startOf("day").toMillis();
}

/**
 * @returns The day buckets regrouped by calendar month, oldest first
 * @param days Day buckets, as returned by `useCaptureDateStats`
 */
export function groupBucketsByMonth(days: CaptureBucket[]): CaptureBucket[] {
  const countsPerMonth = new Map<number, number>();

  for (const { start, count } of days) {
    const month = DateTime.fromJSDate(start).startOf("month").toMillis();
    countsPerMonth.set(month, (countsPerMonth.get(month) ?? 0) + count);
  }

  return Array.from(countsPerMonth, ([start, count]) => ({
    start: new Date(start),
    count,
  })).sort((a, b) => a.start.getTime() - b.start.getTime());
}

/**
 * @returns How many captures fall inside the given range, or `total` when the range is open
 * @param stats The project capture histogram
 * @param start Inclusive start of the range
 * @param end Inclusive end of the range
 */
export function countCapturesInRange(
  stats: CaptureDateStats,
  start: Date | undefined,
  end: Date | undefined,
): number {
  if (!start && !end) return stats.total;
  return captureDaysInRange(stats, start, end).reduce(
    (sum, { count }) => sum + count,
    0,
  );
}

/**
 * @returns The days holding captures inside the given range, oldest first
 * @param stats The project capture histogram
 * @param start Inclusive start of the range, unbounded when undefined
 * @param end Inclusive end of the range, unbounded when undefined
 */
export function captureDaysInRange(
  stats: CaptureDateStats,
  start: Date | undefined,
  end: Date | undefined,
): CaptureBucket[] {
  const from = start ? DateTime.fromJSDate(start).startOf("day") : undefined;
  const to = end ? DateTime.fromJSDate(end).endOf("day") : undefined;

  return stats.days.filter(({ start: day }) => {
    const date = DateTime.fromJSDate(day);
    return (!from || date >= from) && (!to || date <= to);
  });
}
